import { useState } from 'react';
import type { StageEmailTemplate, CandidateEmailRecord } from '@recruitai/shared';
import { pipelineApi } from '@/lib/pipeline-api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Clock } from 'lucide-react';

interface EmailModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  candidateId: string;
  candidateName: string;
  jobTitle: string;
  stageName: string;
  orgName: string;
  emailTemplate?: StageEmailTemplate;
  emailHistory?: CandidateEmailRecord[];
}

function replacePlaceholders(text: string, data: { candidateName: string; jobTitle: string; stageName: string; orgName: string }) {
  return text
    .replace(/\{candidateName\}/g, data.candidateName)
    .replace(/\{jobTitle\}/g, data.jobTitle)
    .replace(/\{stageName\}/g, data.stageName)
    .replace(/\{orgName\}/g, data.orgName);
}

export function EmailModal({ open, onClose, jobId, candidateId, candidateName, jobTitle, stageName, orgName, emailTemplate, emailHistory }: EmailModalProps) {
  const placeholderData = { candidateName, jobTitle, stageName, orgName };
  const [subject, setSubject] = useState(emailTemplate ? replacePlaceholders(emailTemplate.subject, placeholderData) : '');
  const [body, setBody] = useState(emailTemplate ? replacePlaceholders(emailTemplate.body, placeholderData) : '');
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Preencha o assunto e o corpo do email');
      return;
    }
    setSending(true);
    try {
      await pipelineApi.sendCandidateEmail(jobId, candidateId, subject, body);
      toast.success('Email enviado com sucesso');
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao enviar email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" /> Enviar Email para {candidateName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="email-subject">Assunto</Label>
            <Input id="email-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Assunto do email" />
          </div>
          <div>
            <Label htmlFor="email-body">Corpo</Label>
            <Textarea id="email-body" value={body} onChange={e => setBody(e.target.value)} placeholder="Corpo do email..." rows={6} />
            <p className="text-xs text-muted-foreground mt-1">
              Placeholders: {'{candidateName}'}, {'{jobTitle}'}, {'{stageName}'}, {'{orgName}'}
            </p>
          </div>

          <div className="flex justify-between items-center">
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}>
              <Clock className="h-4 w-4 mr-1" /> Histórico ({emailHistory?.length || 0})
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="button" onClick={handleSend} disabled={sending}>
                {sending ? 'Enviando...' : 'Enviar'}
              </Button>
            </div>
          </div>

          {showHistory && emailHistory && emailHistory.length > 0 && (
            <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
              {emailHistory.map((record, i) => (
                <div key={record.emailId || i} className="text-xs border-b pb-2 last:border-0">
                  <div className="flex justify-between">
                    <span className="font-medium">{record.subject}</span>
                    <span className="text-muted-foreground">{new Date(record.sentAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <p className="text-muted-foreground truncate">{record.body}</p>
                  <span className="text-muted-foreground">Por: {record.senderName} · Etapa: {record.stageName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
